import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/app/api";
import { useAuth } from "@/app/AuthContext";
import { Card, StatusBadge } from "@/app/ui";
import { RequestCallButton } from "@/components/RequestCall";
import { BenefitCard } from "@/app/pages/learner/BenefitCard";
import PartnerDashboard from "@/app/pages/partner/PartnerDashboard";
import { FileText, Award, Bookmark, Wallet, ArrowRight, AlertCircle, Bell, Building2, Handshake, Shield } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, sub, to }) => (
  <Link to={to || "#"} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <Card className="transition-transform duration-300 hover:-translate-y-1">
      <div className="flex items-start justify-between">
        <p className="text-sm text-slatey">{label}</p>
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cobalt/10 text-cobalt"><Icon className="h-4.5 w-4.5" /></span>
      </div>
      <p className="mt-3 font-display text-3xl font-black tracking-tight text-ink">{value}</p>
      <p className="mt-1 text-sm text-slatey">{sub}</p>
    </Card>
  </Link>
);

function LearnerDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/individual/dashboard").then((r) => setData(r.data)).catch((e) => setErr("Could not load dashboard")); }, []);
  if (err) return <p className="text-rose-600">{err}</p>;
  if (!data) return <p className="text-slatey">Loading your dashboard…</p>;
  const s = data.stats;
  return (
    <div data-testid="learner-dashboard">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Welcome back, {data.name.split(" ")[0]}</h1>
      <p className="mt-2 text-slatey">Here's what's happening across your learning journey.</p>
      <Link to="/app/discover" className="mt-5 inline-flex items-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03]">
        Discover opportunities <ArrowRight className="h-4 w-4" />
      </Link>

      <Card className="mt-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-bold text-ink">Complete your profile</h2>
            <p className="mt-1 text-sm text-slatey">A complete profile unlocks better matches and faster applications.</p>
          </div>
          <Link to="/app/profile" className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-ink hover:bg-surface">Complete</Link>
        </div>
        <div className="mt-4 h-2.5 w-full rounded-full bg-slate-100">
          <div className="h-2.5 rounded-full bg-cobalt transition-all" style={{ width: `${data.profile_completion}%` }} />
        </div>
        <p className="mt-2 text-xs font-semibold text-slatey">{data.profile_completion}% complete</p>
      </Card>

      <Card className="mt-6" data-testid="dashboard-help-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-bold text-ink">Need some help?</h2>
            <p className="mt-1 text-sm text-slatey">Request a call from the OpenDegree team and we’ll get back to you.</p>
          </div>
          <RequestCallButton sourcePage="dashboard" testId="dashboard-request-call" />
        </div>
      </Card>

      <BenefitCard />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={FileText} label="Applications" value={s.applications.count} sub={`${s.applications.need_attention} need attention`} to="/app/applications" />
        <StatCard icon={Award} label="Offers" value={s.offers.count} sub={s.offers.respond_by ? "Respond soon" : "None yet"} to="/app/applications" />
        <StatCard icon={Bookmark} label="Saved" value={s.saved.count} sub="On your shortlist" to="/app/discover" />
        <StatCard icon={Wallet} label="Funding matches" value={s.funding_matches.count} sub="You may be eligible" to="/app/funding" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="font-display text-lg font-bold text-ink">Required actions</h2>
          <div className="mt-4 divide-y divide-slate-100">
            {data.required_actions.length === 0 && <p className="text-sm text-slatey">You're all caught up.</p>}
            {data.required_actions.map((a, i) => (
              <div key={i} className="flex items-center gap-3 py-3" data-testid={`action-${i}`}>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-50 text-amber-500"><AlertCircle className="h-4.5 w-4.5" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink truncate">{a.title}</p>
                  <p className="text-xs text-slatey truncate">{a.subtitle}</p>
                </div>
                {a.due && <span className="text-xs font-medium text-slatey">{new Date(a.due).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>}
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h2 className="font-display text-lg font-bold text-ink">Recent activity</h2>
          <div className="mt-4 divide-y divide-slate-100">
            {data.notifications.map((n, i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cobalt/10 text-cobalt"><Bell className="h-4 w-4" /></span>
                <div className="min-w-0"><p className="text-sm font-semibold text-ink truncate">{n.title}</p><p className="text-xs text-slatey truncate">{n.subtitle}</p></div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

const ROLE_INFO = {
  provider: { icon: Building2, title: "Provider portal", body: "Manage your programmes, review applicants and track recruitment. Full provider tools arrive in an upcoming phase." },
  partnership: { icon: Handshake, title: "Relationship & Compliance hub", body: "Manage organisations, contacts, partnership pipeline and compliance. Full tooling arrives in an upcoming phase." },
  admin: { icon: Shield, title: "Platform administration", body: "Manage users, organisations, providers, applications and settings. Full admin tools arrive in an upcoming phase." },
};

function RoleDashboard({ user }) {
  const info = ROLE_INFO[user.role];
  const Icon = info.icon;
  return (
    <div data-testid="role-dashboard">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Welcome, {user.name}</h1>
      <p className="mt-2 text-slatey capitalize">{user.role} workspace</p>
      <Card className="mt-8 flex items-start gap-5">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-cobalt/10 text-cobalt"><Icon className="h-7 w-7" /></span>
        <div>
          <h2 className="font-display text-xl font-bold text-ink">{info.title}</h2>
          <p className="mt-2 text-slatey">{info.body}</p>
          <StatusBadge status="under_review" label="Coming soon" />
        </div>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return user.role === "individual" ? <LearnerDashboard /> : user.role === "partnership" ? <PartnerDashboard /> : <RoleDashboard user={user} />;
}
